import React, {
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useRouteMatch, useHistory } from "react-router-dom";
import firebase from "../firebase";
import debounce from "lodash.debounce";

import SkeletonState from "../Components/Forum/SkeletonState";
import Footer from "../Components/Footer";
import PostCard from "../Components/Forum/PostCard";
import ForumPost from "../Components/Forum/ForumPost";
import HotlineCard from "../Components/Forum/HotlineCard";
import Categories from "../Components/Forum/Categories";
import RefreshButton from "../Components/Forum/RefreshButton";
import { ScrollToTop } from "../Components/MenuBarProvider";
import LifeDocLogo from "../Assets/LifeDlogo.png";

import { makeStyles } from "@material-ui/core/Styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";

const useStyles = makeStyles((theme) => ({
  endMessage: {
    padding: 20,
    marginTop: 10,
    marginBottom: 10,
    marginLeft: 5,
  },
  body: {
    height: "auto",
    maxWidth: "100vw",
  },
  footer: {
    marginTop: "auto",
  },
}));

const Forum = () => {
  const classes = useStyles();
  const { handleScroll } = useContext(ScrollToTop);
  const match = useRouteMatch();
  const { path, url, params } = match;
  const history = useHistory();
  const observer = useRef();

  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [hasFetched, setHasFetched] = React.useState(false);
  const [hasData, setHasData] = React.useState(true);
  const [lastKey, setLastKey] = React.useState("");
  const [showRefresh, setRefresh] = React.useState(false);

  const updatePosts = (newPosts) => {
    setPosts(newPosts);
  };

  const isIncluded = (object, key) => {
    let matchFound = null;
    object.forEach((data) => {
      if (data.id === key) {
        matchFound = true;
      } else {
        return;
      }
    });
    return matchFound;
  };

  const refreshPosts = () => {
    const reFetch = debounce(() => {
      if (window.pageYOffset === 0) {
        setTimeout(() => {
          updatePosts([]);
          setLastKey("");
          setHasFetched(false);
          setHasData(true);
          setLoading(true);
        }, 0);
        setTimeout(() => {
          fetchPosts();
        }, 1000);
      } else {
        reFetch();
      }
    }, 800);
    handleScroll();
    setRefresh(false);
    reFetch();
  };

  const fetchPosts = async () => {
    try {
      //Set initial state
      let initialPosts = [];
      //Set loading state
      setLoading(true);
      //Reference to firebase based on url parameter and selects the 5 items from the end
      const subscribePosts = firebase
        .database()
        .ref(`posts/${params.id}`)
        .orderByKey()
        .limitToLast(5);
      //Fetches the value once from the database
      const fetchedPosts = await subscribePosts.once("value");
      //Checks if there is data fetched and pushes it into initial state
      if (fetchedPosts.exists()) {
        fetchedPosts.forEach((data) => {
          const date = new Date(data.val().postsDate).toLocaleDateString();
          const time = new Date(data.val().postsDate).toLocaleTimeString();
          initialPosts.push({
            id: data.key,
            fromPost: data.val().fromPost,
            message: data.val().message,
            postsTimestamp: data.val().postsDate,
            postsDate: date + " at " + time,
            postsEdited: data.val().postsEdited,
            postsName: data.val().postsName,
            postsTitle: data.val().postsTitle,
            postsUserID: data.val().postsUserID,
            voters: data.val().voters ? data.val().voters : {},
            votes: data.val().votes ? data.val().votes : 0,
            urlParam: params.id,
          });
        });
        //Reverse the items stored in the initial state since the latest items are stored in the end of the array
        initialPosts.sort().reverse();
        //Gets the last key as reference for the next fetch
        setLastKey(initialPosts[initialPosts.length - 1].id);
        //Updates the actual state of the component
        updatePosts(initialPosts);
        //Remove loading state and setHasFetched to true to initialize firebase listeners
        setTimeout(() => {
          setHasFetched(true);
          setLoading(false);
        }, 1250);
      } else {
        setHasData(false);
        setHasFetched(true);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      //handle error
    }
  };

  const fetchNextPosts = async () => {
    try {
      //Checks if initial fetch is finished
      if (hasFetched) {
        //Set initial state
        let clonedPosts = [...posts];
        let succeedingPosts = [];
        //Set loading state
        setLoading(true);
        //Reference to firebase based on url parameter starting at the last known referenced item and
        //selects 6 items starting from it
        const subscribePosts = firebase
          .database()
          .ref(`posts/${params.id}`)
          .orderByKey()
          .endAt(lastKey)
          .limitToLast(6);
        //Fethces the next value once from the database
        const fetchedNextPosts = await subscribePosts.once("value");
        //Checks if there is data fetched
        if (fetchedNextPosts.exists()) {
          fetchedNextPosts.forEach((data) => {
            const date = new Date(data.val().postsDate).toLocaleDateString();
            const time = new Date(data.val().postsDate).toLocaleTimeString();
            succeedingPosts.push({
              id: data.key,
              fromPost: data.val().fromPost,
              message: data.val().message,
              postsTimestamp: data.val().postsDate,
              postsDate: date + " at " + time,
              postsEdited: data.val().postsEdited,
              postsName: data.val().postsName,
              postsTitle: data.val().postsTitle,
              postsUserID: data.val().postsUserID,
              voters: data.val().voters ? data.val().voters : {},
              votes: data.val().votes ? data.val().votes : 0,
              urlParam: params.id,
            });
          });
          //Checks if initial state has items
          if (succeedingPosts.length > 1) {
            succeedingPosts.sort().reverse().splice(0, 1);
            setLastKey(succeedingPosts[succeedingPosts.length - 1].id);
            updatePosts([...clonedPosts, ...succeedingPosts]);
            setTimeout(() => {
              setLoading(false);
            }, 1250);
          } else {
            //if succeedingPosts.length === 1, then the only item stored is the duplicate item
            //therefore there are no more data left to fetch in the database
            console.log("No more data");
            setTimeout(() => {
              setLoading(false);
              setHasData(false);
            }, 1250);
          }
        } else {
          //if there is no data fetched
          setTimeout(() => {
            setLoading(false);
            setHasData(false);
          }, 1250);
        }
      }
    } catch (error) {
      console.log(error);
      //handle error
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  ///*
  useEffect(() => {
    setTimeout(() => {
      fetchPosts();
    }, 500);
  }, []);
  //*/

  /* Unecessary
  useEffect(() => {
    //Whenever the url changes, reload page
    return history.listen((location) => {
      //window.location.reload(false);
      //refreshPosts();
    });
  }, [history]);
  */

  useEffect(() => {
    //Reference to firebase based on url parameter
    const listenPostUpdates = firebase
      .database()
      .ref(`posts/${params.id}`)
      .orderByKey();

    //Check if initial fetched is complete before triggering listeners
    if (hasFetched) {
      listenPostUpdates.on("child_added", (data) => {
        if (isIncluded(posts, data.key)) {
          return;
        } else {
          if (hasData) {
            let clonedPosts = [...posts];
            data.val().postsDate > clonedPosts[0].postsTimestamp
              ? setRefresh(true)
              : console.log("There's still more data");
          }
        }
      });
      listenPostUpdates.on("child_changed", (data) => {
        let postIndex = -1;
        posts.forEach((object, index) => {
          if (object.id === data.key) postIndex = index;
        });
        if (postIndex >= 0) {
          let clonedPosts = [...posts];
          let date = new Date(data.val().postsDate).toLocaleDateString();
          let time = new Date(data.val().postsDate).toLocaleTimeString();
          clonedPosts.splice(postIndex, 1, {
            id: data.key,
            fromPost: data.val().fromPost,
            message: data.val().message,
            postsTimestamp: data.val().postsDate,
            postsDate: date + " at " + time,
            postsEdited: data.val().postsEdited,
            postsName: data.val().postsName,
            postsTitle: data.val().postsTitle,
            postsUserID: data.val().postsUserID,
            voters: data.val().voters ? data.val().voters : {},
            votes: data.val().votes ? data.val().votes : 0,
            urlParam: params.id,
          });
          updatePosts(clonedPosts);
        } else if (hasData) {
          //console.log("There's still more data");
          return;
        } else {
          return;
        }
      });
      listenPostUpdates.on("child_removed", (data) => {
        let postIndex = -1;
        posts.forEach((object, index) => {
          if (object.id === data.key) postIndex = index;
        });
        if (postIndex >= 0) {
          let clonedPosts = [...posts];
          clonedPosts.splice(postIndex, 1);
          updatePosts(clonedPosts);
        } else if (hasData) {
          //console.log("There's still more data");
          return;
        } else {
          return;
        }
      });
    }
  }, [hasData, hasFetched]);

  ///*
  //For infinite scrolling using Intersection Observer API
  const lastPostRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasData) {
            //call next fetch
            fetchNextPosts();
          }
        },
        {
          threshold: 1.0,
        }
      );
      if (node) observer.current.observe(node);
    },
    [loading, hasData]
  );

  const mapPosts = (posts) => {
    if (Array.isArray(posts) && posts.length) {
      return (
        <div>
          {posts.map((singlePost, index) => {
            if (posts.length === index + 1) {
              return (
                <div key={singlePost.id} ref={lastPostRef}>
                  <ForumPost {...singlePost} />
                  <Divider variant="middle" />
                </div>
              );
            } else {
              return (
                <div key={singlePost.id}>
                  <ForumPost {...singlePost} />
                  <Divider variant="middle" />
                </div>
              );
            }
          })}
        </div>
      );
    } else {
      if (hasFetched && posts.length === 0) {
        return <div className={classes.endMessage}>Nobody has posted yet.</div>;
      }
    }
  };
  //*/

  /*
  const mapPosts = (posts) => {
    if (Array.isArray(posts) && posts.length) {
      return (
        <div>
          {posts.map((singlePost, index) => {
            return (
              <div key={singlePost.id}>
                <ForumPost {...singlePost} />
                <Divider variant="middle" />
              </div>
            );
          })}
        </div>
      );
    } else {
      if (hasFetched && posts.length === 0) {
        return <div>Nobody has posted yet.</div>;
      }
    }
  };
  */

  return (
    <Container className={classes.body}>
      <RefreshButton showRefresh={showRefresh} refresh={refreshPosts} />
      <Grid
        container
        spacing={0}
        direction="row"
        justify="space-evenly"
        alignItems="flex-start"
      >
        <Grid item lg={2}>
          <Categories />
        </Grid>
        <Grid item lg={8}>
          <Grid
            container
            spacing={3}
            direction="row"
            justify="center"
            alignItems="flex-start"
          >
            <Grid item xs={12}>
              <PostCard />
            </Grid>
            <Grid item xs={12}>
              {/*
                <InfiniteScroll
                error={false}
                hasMore={hasData}
                isLoading={loading}
                initialFetch={fetchPosts}
                nextFetch={fetchNextPosts}
                loader={<SkeletonState forComment={false} />}
                endMessage={
                  <Typography component="div">
                    <Box
                      textAlign="center"
                      fontWeight="fontWeightLight"
                      fontSize={22}
                      letterSpacing={0.4}
                      m={2}
                    >
                      You have seen it all!&nbsp;
                      <span role="img" aria-label="party-face">
                        🥳
                      </span>
                    </Box>
                  </Typography>
                }
                scrollThreshold={0.7}
                dataLength={posts.length}
              >
                {mapPosts(posts)}
              </InfiniteScroll>
              */}
              {useMemo(() => mapPosts(posts), [posts, mapPosts])}
              {loading && <SkeletonState forComment={false} />}
              {!hasData && posts.length > 0 && (
                <Typography component="div">
                  <Box
                    textAlign="center"
                    fontWeight="fontWeightLight"
                    fontSize={22}
                    letterSpacing={0.4}
                    m={2}
                  >
                    You have seen it all!&nbsp;
                    <span role="img" aria-label="party-face">
                      🥳
                    </span>
                  </Box>
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={2}>
          <HotlineCard />
        </Grid>
      </Grid>
      <Container className={classes.footer}>
        <Footer logo={LifeDocLogo} textColor="black" />
      </Container>
    </Container>
  );
};

export default Forum;
